<!--
 * @Description: 平台商户充值列表
-->
<template>
  <div class="merchant-pay">
    <div class="merchant-pay__toolbar">
      <el-form :inline="true" size="small">
        <el-form-item label="选择商户">
          <el-select
            v-model="selectedMerchantId"
            filterable
            clearable
            placeholder="请选择商户"
            style="width: 280px"
            @change="handleMerchantChange"
          >
            <el-option
              v-for="item in merchantOptions"
              :key="item.merchantId"
              :label="item.merchantName"
              :value="item.merchantId"
            />
          </el-select>
        </el-form-item>
      </el-form>
    </div>

    <div v-if="selectedMerchantId" class="merchant-pay__list">
      <div class="merchant-pay__list-header">
        <div class="merchant-pay__list-title">充值明细</div>
        <el-button type="primary" size="small" @click="exportExcel">
          导出Excel
        </el-button>
      </div>
      <Table
        :list-query-params.sync="listQueryParams"
        v-bind="tableAttrs"
        v-on="tableEvent"
      />
    </div>
    <el-empty v-else description="请先选择商户查看充值明细" />
  </div>
</template>

<script>
import Table from "@/components/Table/index.vue";
import XLSX from "xlsx";
import { getPathName } from "@/utils/index.js";
import { platformMerchantRechargeList } from "@/api/business";
import {
  SUMMARY_REMARK,
  fetchActiveMerchants,
  checkPlatformUser,
} from "./utils";

const EXPORT_PAGE_SIZE = 200;
const EXPORT_REQUEST_TIMEOUT = 120000;
const EXPORT_HEADERS = [
  "本次支付(元)",
  "总支付(元)",
  "商户应付(元)",
  "剩余(元)",
  "备注",
  "操作人",
  "时间",
];
const EXPORT_KEYS = [
  "pay_amount",
  "total_amount",
  "total_merchant_amount",
  "remain_amount",
  "remark",
  "operator_name",
  "create_time",
];

export default {
  name: "platformMerchantRechargeList",
  components: {
    Table,
  },
  beforeRouteEnter(to, from, next) {
    if (checkPlatformUser()) {
      next();
    } else {
      next("/404");
    }
  },
  data() {
    return {
      merchantOptions: [],
      selectedMerchantId: "",
      listQueryParams: {
        pageNum: 1,
        pageSize: 10,
        total: 0,
      },
      tableData: [],
      tableConfig: [
        { label: "本次支付(元)", width: "110", value: "pay_amount" },
        { label: "总支付(元)", width: "110", value: "total_amount" },
        { label: "商户应付(元)", width: "110", value: "total_merchant_amount" },
        { label: "剩余(元)", width: "100", value: "remain_amount" },
        { label: "备注", width: "160", value: "remark" },
        { label: "操作人", width: "100", value: "operator_name" },
        { label: "时间", width: "170", value: "create_time" },
      ],
      loadingStatus: false,
    };
  },
  computed: {
    tableAttrs() {
      return {
        config: this.tableConfig,
        tableData: this.tableData,
        loadingStatus: this.loadingStatus,
        isHasButtons: false,
        isShowNumber: true,
        isShowPagination: true,
        rowClassName: this.getRowClassName,
      };
    },
    tableEvent() {
      return {
        subClickPagination: this.getRechargeList,
      };
    },
  },
  created() {
    this.loadMerchants();
  },
  methods: {
    getRowClassName({ row }) {
      return row.isSummary ? "merchant-pay-summary-row" : "";
    },
    async loadMerchants() {
      try {
        this.merchantOptions = await fetchActiveMerchants();
      } catch (error) {
        console.log(error);
        this.merchantOptions = [];
        this.$message.error("加载商户列表失败");
      }
    },
    handleMerchantChange() {
      this.listQueryParams.pageNum = 1;
      if (this.selectedMerchantId) {
        this.getRechargeList();
      } else {
        this.tableData = [];
        this.listQueryParams.total = 0;
      }
    },
    async getRechargeList() {
      if (!this.selectedMerchantId) return;
      try {
        this.loadingStatus = true;
        const { data } = await platformMerchantRechargeList({
          page: this.listQueryParams.pageNum - 1,
          page_size: this.listQueryParams.pageSize,
          merchantId: this.selectedMerchantId,
        });
        const list = (data && data.list) || [];
        this.tableData = list.map((item) => ({
          ...item,
          isSummary: item.remark === SUMMARY_REMARK,
        }));
        this.listQueryParams.total = (data && data.total) || 0;
      } catch (error) {
        console.log(error);
        this.tableData = [];
      } finally {
        this.loadingStatus = false;
      }
    },
    getSelectedMerchantName() {
      const merchant = this.merchantOptions.find(
        (item) => item.merchantId === this.selectedMerchantId
      );
      return (merchant && merchant.merchantName) || this.selectedMerchantId;
    },
    async fetchAllRechargeForExport() {
      const allList = [];
      let page = 0;
      let total = 0;

      while (true) {
        const { data } = await platformMerchantRechargeList(
          {
            page,
            page_size: EXPORT_PAGE_SIZE,
            merchantId: this.selectedMerchantId,
          },
          { timeout: EXPORT_REQUEST_TIMEOUT }
        );
        if (page === 0) {
          total = (data && data.total) || 0;
        }
        const list = (data && data.list) || [];
        if (!list.length) {
          break;
        }
        allList.push(...list);
        page += 1;
        if (total > 0 && allList.length >= total) {
          break;
        }
      }

      return allList;
    },
    async exportExcel() {
      if (!this.selectedMerchantId) {
        this.$message.warning("请先选择商户");
        return;
      }
      const loading = this.$loading({
        lock: true,
        text: "正在导出，请稍候...",
      });
      try {
        const list = await this.fetchAllRechargeForExport();
        if (!list.length) {
          this.$message.warning("暂无数据可导出");
          return;
        }
        const exportData = list.map((item) =>
          EXPORT_KEYS.map((key) => item[key])
        );
        exportData.unshift(EXPORT_HEADERS);
        const ws = XLSX.utils.aoa_to_sheet(exportData);
        ws["!cols"] = [
          { wch: 14 },
          { wch: 14 },
          { wch: 14 },
          { wch: 12 },
          { wch: 20 },
          { wch: 12 },
          { wch: 21 },
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
        const merchantName = this.getSelectedMerchantName();
        XLSX.writeFile(
          wb,
          `平台商户充值_${merchantName}_${getPathName()}.xlsx`
        );
      } catch (error) {
        console.log(error);
        this.$message.error("导出失败，请稍后重试");
      } finally {
        loading.close();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.merchant-pay {
  padding: 20px;

  &__toolbar {
    margin-bottom: 16px;
  }

  &__list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  &__list-title {
    font-size: 15px;
    font-weight: 600;
    color: #333;
  }

  &__list ::v-deep .merchant-pay-summary-row {
    background-color: #e6f7ff !important;

    td {
      color: #096dd9;
      font-weight: 600;
    }
  }
}
</style>
